/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { EuiButtonEmptyProps, EuiButtonIconProps, EuiLinkProps } from '@elastic/eui'
import { EuiButton, EuiButtonEmpty, EuiButtonIcon, EuiLink } from '@elastic/eui'
import type { Props as EuiButtonProps } from '@elastic/eui/src/components/button/button'

import type { MinimalLocation } from '@modules/utils/history'

import type { FunctionComponent } from 'react'

const isModifiedEvent = (event) =>
  !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)

const isLeftClickEvent = (event) => event.button === 0

const isTargetBlank = (event) => {
  const target = event.target.getAttribute('target')
  return target && target !== '_self'
}

type RouteHandler = MinimalLocation | string | (() => string)

interface RouterProps {
  to: RouteHandler
}

function buildComponentProps(
  to: RouteHandler,
  navigate: ReturnType<typeof useNavigate>,
  pathname?: string,
) {
  const toVal = typeof to === 'function' ? to() : to

  function onClick(event) {
    if (event.defaultPrevented) {
      return
    }

    // Let the browser handle links that open new tabs/windows
    if (isModifiedEvent(event) || !isLeftClickEvent(event) || isTargetBlank(event)) {
      return
    }

    // Prevent regular link behavior, which causes a browser refresh.
    event.preventDefault()

    // Push the route to the history.
    navigate(toVal, { state: { from: pathname } })
  }

  const href = typeof toVal === 'object' ? toVal.pathname : toVal
  return [onClick, href]
}

export const CuiRouterLink: FunctionComponent<EuiLinkProps & RouterProps> = ({ to, ...rest }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [onClick, href] = buildComponentProps(to, navigate, pathname)

  const props = { ...rest, href, onClick } as EuiLinkProps
  return <EuiLink {...props} />
}

export const CuiRouterLinkButton: FunctionComponent<EuiButtonProps & RouterProps> = ({
  to,
  ...rest
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [onClick, href] = buildComponentProps(to, navigate, pathname)

  const props = { ...rest, href, onClick } as EuiButtonProps
  return <EuiButton {...props} />
}

export const CuiRouterLinkButtonEmpty: FunctionComponent<EuiButtonEmptyProps & RouterProps> = ({
  to,
  ...rest
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [onClick, href] = buildComponentProps(to, navigate, pathname)

  const props = { ...rest, href, onClick } as EuiButtonEmptyProps
  return <EuiButtonEmpty {...props} />
}

export const CuiRouterLinkButtonIcon: FunctionComponent<EuiButtonIconProps & RouterProps> = ({
  to,
  ...rest
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [onClick, href] = buildComponentProps(to, navigate, pathname)

  const props = { size: 'xs' as const, ...rest, href, onClick } as EuiButtonIconProps
  return <EuiButtonIcon {...props} />
}
